/**
 * うるう年判定
 * @param year 対象年
 * @returns
 */
export function isLeapYear(year: number) {
  // 以下のJavaScriptの仕様を利用することで計算を省いている
  // 0日を指定した場合に前月末を取得する
  return new Date(year, 2, 0).getDate() === 29;
}

/**
 * うるう日を考慮した日付を返す
 * @param year 対象年
 * @param month 対象月
 * @param day 対象日
 * @param isBefore うるう年でない2月29日の場合に前日を返すか
 * @returns
 */
export function fixDate(
  year: number,
  month: number,
  day: number,
  isBefore: boolean
): Date {
  // うるう年ではない2月29日が対象となっているか
  const isLeapTarget = !isLeapYear(year) && month === 2 - 1 && day === 29;
  // うるう年ではない2月29日の場合かつ前日として扱う場合は28日とする
  const nextDay = isLeapTarget && isBefore ? 28 : day;

  // 以下JavaScript仕様を利用することで計算を省いている
  // 2月29日が正常であれば2月29日として扱われる
  // 2月29日が不正であれば3月1日として扱われる
  return new Date(year, month, nextDay);
}

/**
 * 年齢計算
 * @param birthday 誕生日
 * @param targetDay 対象日
 */
export function calcAge(birthday: Date, targetDay: Date): number {
  function convertMMDDNumber(value: Date): number {
    return (value.getMonth() + 1) * 100 + value.getDate();
  }
  const isBeforeBirthday =
    convertMMDDNumber(birthday) > convertMMDDNumber(targetDay);
  return (
    targetDay.getFullYear() -
    birthday.getFullYear() -
    (isBeforeBirthday ? 1 : 0)
  );
}

/**
 * 日付を数値に変換
 * @param value 対象日
 * @returns YYYYMMDD形式の数値
 */
export function convertNumber(value: Date): number {
  return Number(
    `${value.getFullYear()}${(value.getMonth() + 1)
      .toString()
      .padStart(2, '0')}${value
      .getDate()
      .toString()
      .padStart(2, '0')}`
  );
}

/**
 * 開始日と終了日が何日間か取得
 * 本来のほしい値とは違い、1日多い状態で返してしまっている
 * @param from 開始日
 * @param to 終了日
 */
export function convertDays(from: Date, to: Date): number {
  const newFrom = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const newTo = new Date(to.getFullYear(), to.getMonth(), to.getDate());
  // 日付間のミリ秒差 86400000 = 24時間 * 60分 * 60秒 * 1000ミリ秒
  // 対象日数の算出時に開始日が計算に含まれるように1日追加して補正する
  // FIXME: 本来同日の場合は0が想定されるので`+1`をするべきでなかった
  return (newTo.getTime() - newFrom.getTime()) / 86400000 + 1;
}

/**
 * 開始日と終了日が何ヶ月間か取得
 * @param from 開始日
 * @param to 終了日
 * @returns
 */
export function convertMonths(from: Date, to: Date): number {
  const newFrom = new Date(from.getFullYear(), from.getMonth(), 1);
  const newTo = new Date(to.getFullYear(), to.getMonth(), 1);
  return (
    newTo.getFullYear() * 12 +
    newTo.getMonth() -
    (newFrom.getFullYear() * 12 + newFrom.getMonth())
  );
}

/**
 * 文字列形式の日付`YYYY.{0,1}MM.{0,1}DD`からDate型を取得
 * @param value `YYYY.{0,1}MM.{0,1}DD`形式の文字列
 * @param separator セパレーターが存在する場合はtrueを指定
 * @returns
 */
export function convertFromString(value: string, separator = true): Date {
  return new Date(
    Number(value.substr(0, 4)),
    Number(value.substr(separator ? 5 : 4, 2)) - 1,
    Number(value.substr(separator ? 8 : 6, 2))
  );
}

/**
 * 月末の算出
 * @param value 月末を取得する日付
 * @returns 月末の日付
 */
export function convertEndOfTheMonth(value: Date): Date {
  return new Date(value.getFullYear(), value.getMonth() + 1, 0);
}

/**
 * 月末か判定
 * @param value 判定する値
 * @returns
 */
export function isEndOfTheMonth(value: Date): boolean {
  return value.getDate() === convertEndOfTheMonth(value).getDate();
}

/**
 * 月日を入力して一番近い年の年月日を返す
 * @param month 0始まり
 * @param date
 * @returns 一番近い年の年月日
 */
export function nearestDate(month: number, date: number): Date {
  const today = new Date();
  const basedate = new Date().setMonth(month, date);
  const baseyear = new Date(basedate).getFullYear();

  const daysList: number[] = [
    new Date(basedate).setFullYear(baseyear - 1),
    basedate,
    new Date(basedate).setFullYear(baseyear + 1),
  ];

  // 1年おきに3つの日にちを調べて、今日との差が一番小さい日を返す
  const resultTime =
    daysList
      .map((d: number) => d - today.getTime())
      .reduce((previous, current) =>
        Math.abs(previous) < Math.abs(current) ? previous : current
      ) + today.getTime();
  return new Date(resultTime);
}

/**
 * 基となる日付の〇日後を取得
 * @param value 基となる日付
 * @param days 加算する日数
 * @returns
 */
export function addDays(value: Date, days: number): Date {
  const temp = new Date(value);
  temp.setDate(temp.getDate() + days);
  return temp;
}

/**
 * 最小の日付を返す
 * @param values
 * @returns
 */
export function minDate(...values: [Date, Date, ...Date[]]): Date {
  return values.reduce((acc, value) => {
    if (acc.getTime() < value.getTime()) {
      return acc;
    }
    return value;
  }, values[0]);
}

/**
 * 最大の日付を返す
 * @param values
 * @returns
 */
export function maxDate(...values: [Date, Date, ...Date[]]): Date {
  return values.reduce((acc, value) => {
    if (value.getTime() < acc.getTime()) {
      return acc;
    }
    return value;
  }, values[0]);
}

/**
 * 時間を捨てた日付を返す
 * @param value 日付
 * @returns
 */
export function convertDate(value: Date): Date {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

/**
 * 同じ日付時刻か判定
 * @param value1
 * @param value2
 * @returns
 */
export function equalDate(value1: Date, value2: Date): boolean {
  return value1.getTime() === value2.getTime();
}

/**
 * 今日の日付を返す
 * @returns
 */
export function getToday(): Date {
  return convertDate(new Date());
}
